import apiClient from '../client';

// Types
export interface ComplianceDashboard {
  success: boolean;
  dashboard: {
    period_days: number;
    vat_returns: {
      total: number;
      submitted: number;
      pending: number;
      overdue: number;
      next_due_date?: string;
    };
    filings: {
      total: number;
      submitted: number;
      pending: number;
      overdue: number;
      upcoming_count: number;
    };
    gdpr_requests: {
      total: number;
      pending: number;
      in_progress: number;
      completed: number;
      overdue: number;
      average_response_days: number;
    };
    compliance_score: {
      overall: number;
      vat_compliance: number;
      filing_compliance: number;
      gdpr_compliance: number;
      status: string;
    };
  };
}

export interface SalesTaxReturn {
  id: number;
  return_id: string;
  period_key: string;
  period_start: string;
  period_end: string;
  due_date: string;
  vat_due_sales: number;
  vat_due_acquisitions: number;
  total_vat_due: number;
  vat_reclaimed: number;
  net_vat_due: number;
  total_value_sales: number;
  total_value_purchases: number;
  total_value_goods_supplied: number;
  total_acquisitions: number;
  status: string;
  submitted_at?: string;
  submission_reference?: string;
}

export interface ComplianceFiling {
  id: number;
  filing_id: string;
  filing_type: string;
  filing_name: string;
  description?: string;
  period_start?: string;
  period_end?: string;
  due_date: string;
  filing_date?: string;
  status: string;
  priority: string;
  regulatory_body: string;
  submission_reference?: string;
}

export interface GDPRRequest {
  id: number;
  request_id: string;
  request_type: string;
  status: string;
  data_subject_email: string;
  data_subject_name?: string;
  request_details: string;
  due_date: string;
  assigned_to?: string;
  priority: string;
  processed_at?: string;
  processed_by?: string;
  created_at: string;
}

// API Functions
export const complianceService = {
  // Get compliance dashboard
  getDashboard: async (days: number = 30): Promise<ComplianceDashboard> => {
    const response = await apiClient.get(`/compliance/dashboard?days=${days}`);
    return response.data;
  },

  // Sales Tax Returns
  getSalesTaxReturns: async (params?: {
    status?: string;
    period_start?: string;
    period_end?: string;
    skip?: number;
    limit?: number;
  }): Promise<{ returns: SalesTaxReturn[]; total: number }> => {
    const response = await apiClient.get('/compliance/vat-returns', { params });
    return response.data;
  },

  getSalesTaxReturn: async (returnId: string): Promise<{ success: boolean; return: SalesTaxReturn }> => {
    const response = await apiClient.get(`/compliance/vat-returns/${returnId}`);
    return response.data;
  },

  createSalesTaxReturn: async (data: Partial<SalesTaxReturn>): Promise<{ success: boolean; return: SalesTaxReturn }> => {
    const response = await apiClient.post('/compliance/vat-returns', data);
    return response.data;
  },

  submitSalesTaxReturn: async (returnId: string): Promise<{ success: boolean; return: SalesTaxReturn }> => {
    const response = await apiClient.post(`/compliance/vat-returns/${returnId}/submit`);
    return response.data;
  },

  // Compliance Filings
  getFilings: async (params?: {
    filing_type?: string;
    status?: string;
    priority?: string;
    overdue?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ filings: ComplianceFiling[]; total: number }> => {
    const response = await apiClient.get('/compliance/filings', { params });
    return response.data;
  },

  getFiling: async (filingId: string): Promise<{ success: boolean; filing: ComplianceFiling }> => {
    const response = await apiClient.get(`/compliance/filings/${filingId}`);
    return response.data;
  },

  createFiling: async (data: Partial<ComplianceFiling>): Promise<{ success: boolean; filing: ComplianceFiling }> => {
    const response = await apiClient.post('/compliance/filings', data);
    return response.data;
  },

  submitFiling: async (filingId: string, data: any): Promise<{ success: boolean; filing: ComplianceFiling }> => {
    const response = await apiClient.post(`/compliance/filings/${filingId}/submit`, data);
    return response.data;
  },

  // GDPR Requests
  getGDPRRequests: async (params?: {
    request_type?: string;
    status?: string;
    priority?: string;
    overdue?: boolean;
    skip?: number;
    limit?: number;
  }): Promise<{ requests: GDPRRequest[]; total: number }> => {
    const response = await apiClient.get('/compliance/gdpr-requests', { params });
    return response.data;
  },

  getGDPRRequest: async (requestId: string): Promise<{ success: boolean; request: GDPRRequest }> => {
    const response = await apiClient.get(`/compliance/gdpr-requests/${requestId}`);
    return response.data;
  },

  createGDPRRequest: async (data: Partial<GDPRRequest>): Promise<{ success: boolean; request: GDPRRequest }> => {
    const response = await apiClient.post('/compliance/gdpr-requests', data);
    return response.data;
  },

  processGDPRRequest: async (requestId: string, data: {
    response_data: any;
    response_notes?: string;
  }): Promise<{ success: boolean; request: GDPRRequest }> => {
    const response = await apiClient.post(`/compliance/gdpr-requests/${requestId}/process`, data);
    return response.data;
  },

  // Compliance Reports
  generateComplianceReport: async (reportType: string, params?: {
    start_date?: string;
    end_date?: string;
  }): Promise<{ success: boolean; report: any }> => {
    const response = await apiClient.post('/compliance/reports/generate', {
      report_type: reportType,
      ...params,
    });
    return response.data;
  },

  // Upcoming deadlines
  getUpcomingDeadlines: async (days: number = 30): Promise<{
    success: boolean;
    deadlines: Array<{
      type: string;
      name: string;
      due_date: string;
      days_until_due: number;
      priority: string;
    }>;
  }> => {
    const response = await apiClient.get(`/compliance/upcoming-deadlines?days=${days}`);
    return response.data;
  },

  // Compliance score
  getComplianceScore: async (): Promise<{
    success: boolean;
    score: {
      overall: number;
      vat_compliance: number;
      filing_compliance: number;
      gdpr_compliance: number;
      status: string;
      recommendations: string[];
    };
  }> => {
    const response = await apiClient.get('/compliance/score');
    return response.data;
  },
};

export default complianceService;

